import { useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/frontend/components/ui/carousel";
import { ImageLightbox } from "@/frontend/components/residences/ImageLightbox";
import { CommonArea } from "@/shared/types";

interface CommonAreasSectionProps {
  commonAreas?: CommonArea[];
}

export const CommonAreasSection = ({ commonAreas }: CommonAreasSectionProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Flatten every area's photos into a single list of slides, each tagged with
  // its area name/description so the unified carousel can title each photo.
  const slides = (commonAreas ?? [])
    .filter((area) => area.imagesUrl?.length > 0)
    .flatMap((area) =>
      area.imagesUrl.map((url) => ({
        url,
        areaName: area.name,
        description: area.description,
      }))
    );

  if (slides.length === 0) return null;

  return (
    <div>
      <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
        Residence Common Areas
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Shared spaces available to all residents
      </p>
      <div className="relative">
        <Carousel className="w-full" opts={{ loop: slides.length > 1 }}>
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index}>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="relative block w-full aspect-[4/3] bg-muted rounded-xl overflow-hidden cursor-zoom-in"
                >
                  <img
                    src={slide.url}
                    alt={`${slide.areaName} - Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Slide counter */}
                  {slides.length > 1 && (
                    <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs font-medium text-white">
                      {index + 1} / {slides.length}
                    </div>
                  )}
                  {/* Title overlay so each photo stays tied to its area */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-left">
                    <p className="text-white font-heading font-semibold">
                      {slide.areaName}
                    </p>
                    {slide.description && (
                      <p className="text-white/80 text-sm line-clamp-2">
                        {slide.description}
                      </p>
                    )}
                  </div>
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          {slides.length > 1 && (
            <>
              <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2" />
              <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2" />
            </>
          )}
        </Carousel>
      </div>

      <ImageLightbox
        images={slides.map((slide) => ({
          url: slide.url,
          caption: slide.description
            ? `${slide.areaName} — ${slide.description}`
            : slide.areaName,
        }))}
        open={lightboxIndex !== null}
        startIndex={lightboxIndex ?? 0}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
        title="Common areas"
      />
    </div>
  );
};
